import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import { DataTable, type Column } from '../components/data-table';

interface OwnerActionRow {
  readonly id: string;
  readonly ownerEmail: string;
  readonly method: string;
  readonly path: string;
  readonly targetType: string | null;
  readonly targetId: string | null;
  readonly statusCode: number | null;
  readonly payload: unknown;
  readonly ip: string | null;
  readonly userAgent: string | null;
  readonly createdAt: string;
}

interface Response {
  readonly rows: readonly OwnerActionRow[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

const PAGE_SIZE = 50;

function statusChip(code: number | null) {
  if (code === null) return <span className="nw-chip">—</span>;
  if (code >= 500) return <span className="nw-chip nw-chip--error">{code}</span>;
  if (code >= 400) return <span className="nw-chip nw-chip--warning">{code}</span>;
  if (code >= 200) return <span className="nw-chip nw-chip--success">{code}</span>;
  return <span className="nw-chip">{code}</span>;
}

function methodChip(m: string) {
  const cls =
    m === 'DELETE'
      ? 'nw-chip nw-chip--method nw-chip--error'
      : m === 'POST'
      ? 'nw-chip nw-chip--method nw-chip--success'
      : m === 'PATCH' || m === 'PUT'
      ? 'nw-chip nw-chip--method nw-chip--info'
      : 'nw-chip nw-chip--method';
  return <span className={cls}>{m}</span>;
}

const columns: readonly Column<OwnerActionRow>[] = [
  {
    key: 'createdAt',
    header: 'When',
    width: '180px',
    render: (r) => (
      <span title={r.createdAt} style={{ color: 'var(--text-secondary)' }}>
        {formatDistanceToNow(new Date(r.createdAt), { addSuffix: true })}
      </span>
    ),
  },
  {
    key: 'ownerEmail',
    header: 'Owner',
    render: (r) => <span style={{ fontWeight: 500 }}>{r.ownerEmail}</span>,
  },
  {
    key: 'method',
    header: 'Method',
    width: '90px',
    render: (r) => methodChip(r.method),
  },
  {
    key: 'path',
    header: 'Endpoint',
    render: (r) => (
      <code
        style={{
          fontFamily: 'SF Mono, Menlo, Consolas, monospace',
          fontSize: 'var(--fs-sm)',
        }}
      >
        {r.path}
      </code>
    ),
  },
  {
    key: 'target',
    header: 'Target',
    width: '180px',
    render: (r) =>
      r.targetType ? (
        <>
          <span className="nw-chip">{r.targetType}</span>{' '}
          {r.targetId && (
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
              {r.targetId.slice(0, 8)}…
            </span>
          )}
        </>
      ) : (
        '—'
      ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '80px',
    align: 'center',
    render: (r) => statusChip(r.statusCode),
  },
];

export function AuditOwnerActions() {
  const [page, setPage] = useState(1);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [targetType, setTargetType] = useState('');

  const params = { page, pageSize: PAGE_SIZE, ownerEmail, targetType };
  const query = useQuery({
    queryKey: queryKeys.audit.ownerActions(params),
    queryFn: async () => {
      const search = new URLSearchParams();
      search.set('page', String(page));
      search.set('pageSize', String(PAGE_SIZE));
      if (ownerEmail) search.set('ownerEmail', ownerEmail);
      if (targetType) search.set('targetType', targetType);
      const res = await apiClient.get<Response>(
        `/owner/audit/owner-actions?${search.toString()}`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
    refetchInterval: 30_000,
  });

  const data = query.data;

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Audit · Owner actions</h1>
        <p className="nw-page-header__subtitle">
          Append-only record of every owner-only mutation in admin-api.
          Refreshes every 30 s.
        </p>
      </header>

      <div className="nw-filters">
        <input
          className="nw-filters__input"
          placeholder="Owner email"
          value={ownerEmail}
          onChange={(e) => {
            setPage(1);
            setOwnerEmail(e.target.value);
          }}
        />
        <input
          className="nw-filters__input"
          placeholder="Target type (channel, article, …)"
          value={targetType}
          onChange={(e) => {
            setPage(1);
            setTargetType(e.target.value);
          }}
        />
      </div>

      <DataTable
        columns={columns}
        rows={data?.rows ?? []}
        rowKey={(r) => r.id}
        total={data?.total}
        page={data?.page ?? page}
        pageSize={data?.pageSize ?? PAGE_SIZE}
        onPageChange={setPage}
        loading={query.isLoading || query.isFetching}
        error={query.error ? (query.error as Error).message : null}
        emptyMessage="No owner actions yet. Mutations show up here as soon as they run."
      />
    </>
  );
}
